import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { getEnvVar } from '@mc/client-env'
import {
  Button,
  IconChatActive,
  IconChevronDownDefault,
} from '@mc/design-system'
import { useExperience } from '@mc/experience'
import { useBucket, useExperimentVariables } from '@mc/experiments-react'
import { reportError } from '@mc/next-newrelic'
import { useWidgetHandler } from '../../hooks'
import { BrandKey, brandKeysByRoute } from '../../utils'
import classes from './GladChat.module.scss'

declare global {
  interface Window {
    __onHelpAppHostReady__?: (app: typeof Gladly) => void
  }
}

/**
 * :info: This button id cannot be change. Gladly expects that name.
 * https://github.com/gladly/sidekick-examples/tree/master/docs/custom-minimized-button#custom-minimized-button-setup-steps
 */
const GLAD_CHAT_BUTTON_ID = 'custom-gladly-chat-button'
const getGladChatAppId = (brandKey?: BrandKey) =>
  getEnvVar(
    brandKey ? `GLAD_CHAT_${brandKey}_APP_ID` : 'GLAD_CHAT_HUB_APP_ID',
  ) as string
const GLAD_CHAT_CDN_URL = getEnvVar('GLAD_CHAT_CDN_URL') as string

export const GladChatImpl = ({ brandKey }: { brandKey?: BrandKey }) => {
  const router = useRouter()
  const { widget } = router.query

  const [scriptState, setScriptState] = useState<
    'loading' | 'loaded' | 'error' | 'fully-initialized'
  >('loading')
  const { chatState, setChatState } = useWidgetHandler({
    widgetType: 'Glad App',
  })

  const src = useMemo(
    () => `${GLAD_CHAT_CDN_URL}?q=${new Date().getTime()}`,
    [],
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    loadGladApp(GLAD_CHAT_CDN_URL)
  }, [])

  useEffect(() => {
    if (scriptState !== 'loaded' || typeof window === 'undefined') return

    try {
      window.Gladly.init({
        appId: getGladChatAppId(brandKey),
      })
        .then(() => {
          /**
            The function inside 'then()' will be called once Sidekick is fully
            initialized and loaded to be used. Events can only be subscribed to
            after Sidekick has been initialized and loaded to be used.

            More events: https://developer.gladly.com/glad-app/Gladly.html#event:availability:change
          */

          window.Gladly.on('sidekick:closed', () =>
            setChatState((oldState) =>
              oldState !== 'initially-closed' ? 'closed' : oldState,
            ),
          )
          window.Gladly.on('sidekick:opened', () =>
            setChatState((oldState) =>
              oldState !== 'open-from-url' ? 'open' : oldState,
            ),
          )
          setScriptState('fully-initialized')
        })
        .catch((e) => {
          reportError(e, {
            description: 'There was an error initializing the Gladly Chat SDK.',
          })
          setScriptState('error')
        })
    } catch (e) {
      reportError(e, {
        description: 'There was an error loading the Gladly Chat SDK.',
      })
      setScriptState('error')
    }
  }, [brandKey, scriptState, widget, setChatState])

  const onShowChat = () => {
    if (chatState.includes('closed')) window.Gladly.show()
    else if (chatState.includes('open')) window.Gladly.close()
  }

  useEffect(() => {
    if (scriptState === 'fully-initialized' && chatState === 'open-from-url') {
      window.Gladly.show()
    }
  }, [scriptState, chatState])

  return (
    <>
      <Script
        src={src}
        strategy='lazyOnload'
        onLoad={() => setScriptState('loaded')}
        onError={(e) => {
          reportError(e, {
            description: 'There was an error loading the Gladly Chat SDK.',
          })
          setScriptState('error')
        }}
      />

      {scriptState === 'fully-initialized' && (
        <Button
          id={GLAD_CHAT_BUTTON_ID}
          onClick={onShowChat}
          kind='primary'
          className={classes.button}
        >
          {chatState.includes('closed') ? (
            <IconChatActive size={4} />
          ) : (
            <IconChevronDownDefault size={4} />
          )}
        </Button>
      )}
    </>
  )
}

/**
 * :info: original code: https://developer.gladly.com/glad-app/Gladly.html#~init():~:text=Gladly%20Glad%20App.-,JavaScript%20snippet,-Add%20the%20following
 * refactored in order to use next resources
 */
/* eslint-disable */
const loadGladApp = (cdn: string) => {
  if (window.Gladly) return

  const p: { type: 't' | 'c'; next: unknown }[] = []
  let initArgs: IArguments

  window.Gladly = {
    init: function () {
      initArgs = arguments
      var e = {
        then: function (t: unknown) {
          return p.push({ type: 't', next: t }), e
        },
        catch: function (t: unknown) {
          return p.push({ type: 'c', next: t }), e
        },
      }
      return e
    },
  } as unknown as typeof Gladly

  window.__onHelpAppHostReady__ = function (t: any) {
    delete window.__onHelpAppHostReady__
    window.Gladly = t
    window.Gladly.loaderCdn = cdn
    if (initArgs)
      for (var e = t.init.apply(t, initArgs), n = 0; n < p.length; n++) {
        var a = p[n]
        e = 't' === a.type ? e.then(a.next) : e.catch(a.next)
      }
  }
}
/* eslint-enable */

export const GladChat = () => {
  const forceEnabled =
    useBucket('dais-2248-decagon-ai-chat-in-help-center') === 'off'

  const { brands: decagonEnabledByBrand } = useExperimentVariables(
    'dais-2248-decagon-ai-chat-in-help-center',
  )

  const { isReady: isExperienceReady, isEnterprise } = useExperience()

  const router = useRouter()
  const { slug } = router.query
  const brandKey = brandKeysByRoute[
    slug as keyof typeof brandKeysByRoute
  ] as BrandKey

  // If Decagon is enabled then we want to have Gladly disabled
  const disabledForBrand = slug
    ? (decagonEnabledByBrand as Record<BrandKey, boolean>)?.[brandKey] ?? false
    : true

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!forceEnabled && disabledForBrand) {
      window.Gladly?.close?.()
    }
  }, [slug, disabledForBrand, forceEnabled])

  if (
    !isExperienceReady ||
    (!isEnterprise && !forceEnabled && disabledForBrand)
  ) {
    return null
  }

  return <GladChatImpl brandKey={brandKey} />
}
